import React, { useState, useEffect } from 'react';
import { useServiceCategories } from '../../context/ServiceCategoryContext';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import Pagination from '../common/Pagination';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';
import { useRoutePath } from '../../utils/getRoutePath';

const ServiceCategoriesList = () => {
  const { serviceCategories, deleteServiceCategory, getServiceCategories, paginate } = useServiceCategories();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const [showAddBtn, setShowAddBtn] = useState(true);
  const [showDeleteBtn, setShowDeleteBtn] = useState(true);
  const [filteredServiceCategories, setFilteredServiceCategories] = useState([]);
  const getRoutePath = useRoutePath();  
  const [totalPages, setTotalPages] = useState(1);
 
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('page')) || 1;
    setCurrentPage(page)
    
    if(user.role_id !== 1 && serviceCategories?.data?.length >= 1){
      setShowAddBtn(true)
    }
    if(user.role_id !== 1){
      setShowDeleteBtn(true)
    }
 
    setSearchTerm(params.get('q') || '')
    setStatusFilter(params.get('active') || '')

  },[user, serviceCategories, showAddBtn])

  const filtersData = (key, value, arrayFilters = []) => {
    const applyFilters = async (filterDataVal) => {
      let filters = {};
      const newUrl = new URL(window.location);
      if (filterDataVal['q']?.key === 'q') {
        filters.q = filterDataVal['q']?.value;
        if(filterDataVal['q']?.value){
          setSearchTerm(filterDataVal['q'].value)
          newUrl.searchParams.set('q', filterDataVal['q'].value);
        }else{
          newUrl.searchParams.delete('q');
        }
      }else if(searchTerm){
        filters.q = searchTerm;
        newUrl.searchParams.set('q', searchTerm);
      } else{
        newUrl.searchParams.delete('q');
      }
  
      if(filterDataVal['active']?.key === 'active'){
        filters.active = filterDataVal['active']?.value;
        if(filterDataVal['active']?.value){
          setStatusFilter(filterDataVal['active'].value)
          newUrl.searchParams.set('active', filterDataVal['active'].value);
        }else{
          newUrl.searchParams.delete('active');
        }
      }else if(statusFilter){
        filters.active = statusFilter;
        newUrl.searchParams.set('active', statusFilter);
      } else{
        newUrl.searchParams.delete('active');
      }
      newUrl.searchParams.delete('page');
      window.history.pushState({}, '', newUrl);
      
      const params = new URLSearchParams(window.location.search);
      const page = parseInt(params.get('page')) || 1;
      if(Object.keys(filters).length > 0){
        let response = await getServiceCategories(page, filters);
       
        if(response?.data){
          setTotalPages(response?.last_page ? response.last_page : 1)
          setFilteredServiceCategories(response?.data);
          setCurrentPage(page)
        }
      }
    };
 
    let filterDataVal = {};
    if(arrayFilters.length > 0){
      arrayFilters.forEach(filter => {
        filterDataVal[filter.key] = {
          key:filter.key,value:filter.value
        } 
      })
    }else{
      filterDataVal[key] = {
        key:key,value:value
      } 
    }
    applyFilters(filterDataVal);
  }

  useEffect(() => {
     
    setTotalPages(serviceCategories?.last_page ? serviceCategories.last_page : 1)
    
    if (serviceCategories?.data) {
      let filtered = [...serviceCategories.data];
       
      // Apply sorting
      if (sortConfig.key) {
        filtered.sort((a, b) => {
          let aValue = a[sortConfig.key];
          let bValue = b[sortConfig.key];
          
          if (sortConfig.key.includes('.')) {
            const fields = sortConfig.key.split('.');
            aValue = fields.reduce((obj, field) => obj?.[field], a);
            bValue = fields.reduce((obj, field) => obj?.[field], b);
          }

          if (aValue === undefined || aValue === null) aValue = '';
          if (bValue === undefined || bValue === null) bValue = '';

          if (typeof aValue === 'boolean') {
            return sortConfig.direction === 'asc' ? (aValue ? 1 : -1) : (aValue ? -1 : 1);
          }

          if (sortConfig.key === 'created_at' || sortConfig.key === 'updated_at') {
            const dateA = new Date(aValue);
            const dateB = new Date(bValue);
            return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
          }

          return sortConfig.direction === 'asc'
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        });
      }
     
      setFilteredServiceCategories(filtered);
    }
  }, [serviceCategories, searchTerm, statusFilter, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Calculate pagination using serviceCategories values
  const currentItems = filteredServiceCategories;
  const handlePageChange = (page) => {
    setCurrentPage(page);
    let filters = {}; 
    if(searchTerm){
      filters.q = searchTerm;
    }
    if(statusFilter){
      filters.active = statusFilter;
    }
     
    getServiceCategories(page, filters);
    // Update the URL with ?page={page}
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('page', page);
    window.history.pushState({}, '', newUrl);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        deleteServiceCategory(id).then(response => {
          if(response.status === 200){
            Swal.fire(
              'Deleted!',
              'Service Category has been deleted.',
              'success'
            );
          }else{
            Swal.fire(
              'Error!',
              'Service Category has not been deleted.',
              'error'
            );
          }
        });
      }
    });
  };

  const SortableHeader = ({ columnKey, children }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
      onClick={() => handleSort(columnKey)}
    >
      <div className="flex items-center">
        {children}
        {sortConfig.key === columnKey && (
          <span className="ml-1">
            {sortConfig.direction === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  return (
    <div className="py-6">
      <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8 mb-3">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-gray-900">Service Categories</h1>
          {showAddBtn && (
            <Link 
              to={getRoutePath('/service-categories/create')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add New Category
            </Link>
          )}
        </div>
      </div>

      <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="bg-white rounded-lg shadow-md mb-3">
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 p-5 gap-4">
            <div>
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => filtersData('q', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => filtersData('active', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Service Category Details</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Service category information and details.</p>
          </div>
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto scrollbar-thin">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader columnKey="name">Category Name</SortableHeader>
                    <SortableHeader columnKey="parent.name">Parent Category</SortableHeader>
                     <SortableHeader columnKey="active">Status</SortableHeader>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                        <div className="flex flex-col items-center justify-center space-y-4">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                            <PlusIcon className="w-8 h-8 text-gray-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Service Categories Found</h3>
                            <p className="text-gray-500 mb-4">Get started by adding a new service category</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((category) => (
                      <tr key={category.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-gray-200 mr-3 flex items-center justify-center">
                              {category.display_image?.path ? (
                                <img
                                  src={category.display_image.path}
                                  alt={category.name}
                                  className="h-10 w-10 rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-gray-500 text-sm">
                                  {category.name?.[0]?.toUpperCase() || 'C'}
                                </span>
                              )}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {category.name || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {category.formated_created_at || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{category.parent?.name || 'N/A'}</div>
                          <div className="text-sm text-gray-500">
                            {category.parent ? 'Sub Category' : 'Main Category'}
                          </div>
                        </td>
                         
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            category.active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {category.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                              <Link 
                                  to={getRoutePath(`/service-categories/view/${category.id}`)}
                                  className="text-[#009efb] hover:text-[#0089db]"
                                  title="View"
                                >
                                  <EyeIcon className="w-4 h-4 inline" />
                              </Link>

                            <Link 
                              to={getRoutePath(`/service-categories/edit/${category.id}`)}
                              className="text-[#009efb] hover:text-[#0089db] mr-4"
                            >
                              <PencilIcon className="w-4 h-4 inline" />
                            </Link>
                            {showDeleteBtn && (
                              <button
                                onClick={() => handleDelete(String(category.id))}
                                className="text-red-600 hover:text-red-900"
                              >
                                <TrashIcon className="w-4 h-4 inline" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {currentItems.length > 0 && paginate && (
              <div className="px-4 py-3 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceCategoriesList; 